import { differenceInMinutes, format } from 'date-fns';
import { Pressable, type StyleProp, StyleSheet, View, type ViewStyle } from 'react-native';
import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { cardSurface } from '../../../theme/surfaces';
import { tokens } from '../../../theme/tokens';
import type { CalendarEvent } from '../../../types/calendar';
import { useEventPreview } from '../hooks/useEventPreview';
import {
  EventSourceIndicator,
  useEventSourceAccessibilityLabel,
} from '../indicators/EventSourceIndicator';
import { type EventStatusTone, getEventAppearance } from '../utils/calendarEventAppearance';
import { EventPreviewHost } from './EventPreviewHost';

import { JoinCallButton } from './JoinCallButton';

/**
 * What the caller wires up so the block can be dragged. Supplied by the grid,
 * which is the only thing that knows the day window and the grid height.
 */
export interface DragBinding {
  canDrag: boolean;
  isDragging: boolean;
  onDragStart: (y: number) => void;
  onResizeStart: (y: number) => void;
}

// The status chip is the non-colour half of the status signal, so it has to
// survive Android, where `borderStyle: 'dashed'` silently renders solid once a
// radius is set. A Badge carries a real translated word, not just a tint.
const STATUS_TONE_COLORS: Record<Exclude<EventStatusTone, 'none'>, 'green' | 'red' | 'neutral'> = {
  success: 'green',
  error: 'red',
  muted: 'neutral',
};

interface CalendarDayEventBlockProps {
  event: CalendarEvent;
  onPress?: (event: CalendarEvent) => void;
  /**
   * Desktop-only drag-to-reschedule. Absent (or on a phone) the block is a
   * plain pressable — see useEventDrag for why mobile has no drag.
   */
  drag?: DragBinding;
  /** Position/size style computed by the CALLER (CalendarDayTimeline for a
   * single day, Unit 5's week grid for a day-column + overlap slot) — this
   * component owns interaction and presentation only, never its own layout
   * position, so Unit 5 can reuse it unchanged for each of its 7 columns. */
  style?: StyleProp<ViewStyle>;
  /** The event's real start/end falls outside the visible window (see
   * calendarDayGeometry.ts) — drawn as a dashed edge so a viewer can tell it
   * continues off-screen, on top of the geometry already clamping it in place. */
  clippedStart?: boolean;
  clippedEnd?: boolean;
  /** Lets the all-day strip (CalendarDayTimeline) reuse this exact
   * interaction wiring under its own testID namespace, distinct from the
   * positioned hour-grid blocks. */
  testIDPrefix?: string;
}

// Reuses useEventPreview + EventPreviewHost completely unchanged — the exact
// same wiring as EventCard.tsx's day-list row: tap opens the edit form via
// onPress, hover(web)/long-press(native) opens the preview via triggerProps.
// What differs from EventCard is only what's INSIDE the trigger (a compact
// block instead of a full list card) and that position/size is owned by the
// caller instead of normal document flow.
export function CalendarDayEventBlock({
  event,
  onPress,
  drag,
  style,
  clippedStart,
  clippedEnd,
  testIDPrefix = 'calendar_day_event',
}: Readonly<CalendarDayEventBlockProps>) {
  const { t } = useTranslation();
  const { previewEvent, triggerProps, dismiss } = useEventPreview(event);
  const sourceLabel = useEventSourceAccessibilityLabel(event.source);

  const startsAt = new Date(event.startsAt);
  const endsAt = new Date(event.endsAt);
  const appearance = getEventAppearance(
    event.source,
    event.status,
    differenceInMinutes(endsAt, startsAt),
  );
  const statusLabel = appearance.statusLabelKey ? t(appearance.statusLabelKey) : null;

  const accessibilityLabel = [
    event.title,
    `${format(startsAt, 'h:mm a')} to ${format(endsAt, 'h:mm a')}`,
    sourceLabel,
    // A nested Badge would render but never be announced — a parent
    // accessibilityLabel overrides its children's — so the status word has to
    // be folded in here, exactly as the source phrase already is.
    statusLabel,
  ]
    .filter(Boolean)
    .join(', ');

  const triggerStyle = StyleSheet.flatten([
    styles.trigger,
    (clippedStart || clippedEnd) && styles.clipped,
  ]);

  return (
    <View testID={`${testIDPrefix}_${event.id}_container`} style={style}>
      <Pressable
        testID={`${testIDPrefix}_${event.id}`}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        onPress={() => onPress?.(event)}
        // Mouse-down starts a drag; the grid owns the move/up listeners, so a
        // gesture that leaves the block still finishes. Suppressed while
        // dragging so the hover preview does not open under the cursor.
        {...(drag?.canDrag
          ? {
              onStartShouldSetResponder: () => true,
              // biome-ignore lint/suspicious/noExplicitAny: RN Web forwards the DOM mouse event, whose pageY is not in the RN responder type.
              onResponderGrant: (nativeEvent: any) =>
                drag.onDragStart(nativeEvent?.nativeEvent?.pageY ?? 0),
            }
          : {})}
        {...(drag?.isDragging ? {} : triggerProps)}
        style={triggerStyle}
      >
        <Box
          testID={`${testIDPrefix}_${event.id}_surface`}
          flex={1}
          padding="xxs"
          style={[
            cardSurface(),
            styles.surface,
            // Source picks the hue, status can override it: a cancelled
            // meeting is no longer "a voice-agent meeting", it is cancelled.
            { backgroundColor: appearance.fill, borderColor: appearance.borderColor },
          ]}
        >
          <VStack spacing="xxs" style={styles.body}>
            {/* Icon-only, and the phrase folded into the Pressable's label above:
                a parent accessibilityLabel overrides its children's, so an
                indicator nested here would render but never be announced. A
                30-minute block is also too short for a labelled badge. */}
            <HStack spacing="xxs" align="center">
              <EventSourceIndicator
                source={event.source}
                size="sm"
                iconOnly
                testID={`${testIDPrefix}_${event.id}_source`}
              />
              <Text
                testID={`${testIDPrefix}_${event.id}_title`}
                variant="caption"
                numberOfLines={1}
                color={appearance.titleColor}
                style={[
                  styles.title,
                  appearance.strikeThrough && { textDecorationLine: 'line-through' },
                ]}
              >
                {/* Too short for the chip's own row, but the status is the
                    only non-colour signal some treatments (confirmed,
                    no_show) carry — so it rides the title row instead of
                    disappearing. Zero extra height: this row already exists. */}
                {appearance.statusPlacement === 'inline' && statusLabel
                  ? `${statusLabel} · ${event.title}`
                  : event.title}
              </Text>
            </HStack>

            {/* What renders below the title depends on the block's actual
                height, not only the compact/roomy binary: at HOUR_HEIGHT a
                45-71 minute block is roomy enough for a title but not for
                every row. Priority is Join > status > time — the block's
                position on the grid already says *when* it is, but nothing
                else says its status — see calendarEventAppearance.ts. */}
            {appearance.showTime && (
              <Text
                testID={`${testIDPrefix}_${event.id}_time`}
                variant="caption"
                numberOfLines={1}
                color={appearance.metaColor}
                style={styles.time}
              >
                {`${format(startsAt, 'h:mm a')} – ${format(endsAt, 'h:mm a')}`}
              </Text>
            )}

            {appearance.statusPlacement === 'badge' &&
              statusLabel &&
              appearance.statusTone !== 'none' && (
                <Badge
                  testID={`${testIDPrefix}_${event.id}_status`}
                  variant="subtle"
                  color={STATUS_TONE_COLORS[appearance.statusTone]}
                  size="sm"
                >
                  {statusLabel}
                </Badge>
              )}

            {appearance.showJoin && (
              <JoinCallButton conferenceUrl={event.conferenceUrl} eventId={event.id} />
            )}
          </VStack>
        </Box>
      </Pressable>

      <EventPreviewHost event={previewEvent} onDismiss={dismiss} />
    </View>
  );
}

const styles = StyleSheet.create({
  trigger: {
    flex: 1,
  },
  surface: {
    borderRadius: tokens.radius.smPlus,
  },
  // The block's height is the caller's (geometry lives outside this
  // component), so the content clips rather than pushing the box open.
  body: {
    overflow: 'hidden',
  },
  title: {
    fontWeight: '600',
    flexShrink: 1,
  },
  time: {
    fontVariant: ['tabular-nums'],
  },
  clipped: {
    borderStyle: 'dashed',
    borderWidth: 2,
    borderColor: colors.primary[500],
  },
});
